/**
 * Hook to fetch Rich Text links (embedded entries/assets) separately
 * This avoids query complexity issues by not including links in main page query
 */

import {useQuery} from '@tanstack/react-query';
import {print} from 'graphql';
import gql from 'graphql-tag';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';

const RICH_TEXT_LINKS_QUERY = gql`
  query RichTextBlockLinks($id: String!, $locale: String, $preview: Boolean = false) {
    richTextBlock(id: $id, locale: $locale, preview: $preview) {
      content {
        links {
          entries {
            block {
              __typename
              sys {
                id
              }
            }
          }
          assets {
            block {
              sys {
                id
              }
              contentType
              description
              fileName
              height
              width
              size
              title
              url
            }
          }
        }
      }
    }
  }
`;

/**
 * Fetch links for a RichTextBlock separately
 * @param {string} id - RichTextBlock sys.id
 * @param {Object} options - Query options
 * @returns {Object} - { links, isLoading, error }
 */
export const useRichTextLinks = (id, options = {}) => {
  const contentfulService = useService(ContentfulService);

  const {data, isLoading, error} = useQuery({
    queryKey: [
      contentfulService?.key,
      'richTextLinks',
      id,
      contentfulService?.locale
    ],

    queryFn: async () => {
      if (!id || !contentfulService) return null;

      const response = await contentfulService.query(
        print(RICH_TEXT_LINKS_QUERY),
        {
          id,
          locale: contentfulService.locale,
          preview: false,
        }
      );

      return response?.richTextBlock?.content?.links || null;
    },

    enabled: !!id && !!contentfulService && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  return {
    links: data,
    isLoading,
    error,
  };
};

export default useRichTextLinks;
